import React, { useState } from 'react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const ROUNDING_OPTIONS = [
  { value: 2, label: '2 decimals (1,234.56)' },
  { value: 0, label: 'Whole dollars (1,235)' },
];

const NEGATIVE_OPTIONS = [
  { value: 'parens', label: '(1,234)' },
  { value: 'minus', label: '-1,234' },
];

const THOUSANDS_OPTIONS = [
  { value: 'comma', label: 'Comma (1,234,567)' },
  { value: 'space', label: 'Space (1 234 567)' },
  { value: 'period', label: 'Period (1.234.567)' },
];

const CURRENCY_DISPLAY_OPTIONS = [
  { value: 'symbol', label: '$' },
  { value: 'code', label: 'CAD' },
  { value: 'both', label: '$CAD' },
];

function formatSample(value, { rounding, negative, thousands, currencyDisplay }) {
  const absVal = Math.abs(value);
  const rounded = rounding === 0 ? Math.round(absVal) : absVal.toFixed(2);
  const parts = rounded.toString().split('.');
  const intPart = parts[0];
  const decPart = parts[1];

  const sep = thousands === 'comma' ? ',' : thousands === 'space' ? ' ' : '.';
  const decSep = thousands === 'period' ? ',' : '.';

  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const withDec = decPart ? formatted + decSep + decPart : formatted;

  const prefix = currencyDisplay === 'symbol' ? '$' : currencyDisplay === 'code' ? 'CAD ' : '$CAD ';

  if (value < 0) {
    if (negative === 'parens') return prefix + '(' + withDec + ')';
    return prefix + '-' + withDec;
  }
  return prefix + withDec;
}

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

export default function NumberFormatSettings() {
  const t = useTheme();
  const savedFmt = JSON.parse(localStorage.getItem('finkloz_number_format') || 'null');
  const [rounding, setRounding] = useState(savedFmt?.rounding ?? 2);
  const [negative, setNegative] = useState(savedFmt?.negative || 'parens');
  const [thousands, setThousands] = useState(savedFmt?.thousands || 'comma');
  const [currencyDisplay, setCurrencyDisplay] = useState(savedFmt?.currencyDisplay || 'symbol');
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (fn) => (val) => { fn(val); setDirty(true); };

  const sampleValue = -1234567.89;
  const positiveValue = 1234567.89;
  const opts = { rounding, negative, thousands, currencyDisplay };

  const handleSave = () => {
    localStorage.setItem('finkloz_number_format', JSON.stringify({
      rounding, negative, thousands, currencyDisplay,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Number Formatting
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Configure how numbers are displayed across all reports and exports.
      </p>

      {/* Rounding */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Rounding</label>
        <select
          value={rounding}
          onChange={e => set(setRounding)(Number(e.target.value))}
          style={inputStyle(t)}
        >
          {ROUNDING_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Negative Display */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Negative Number Display</label>
        <div style={{ display: 'flex', gap: 12 }}>
          {NEGATIVE_OPTIONS.map(o => (
            <label key={o.value} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="negative"
                value={o.value}
                checked={negative === o.value}
                onChange={() => set(setNegative)(o.value)}
                style={{ accentColor: green }}
              />
              <span style={{ fontFamily: mono }}>{o.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Thousands Separator */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Thousands Separator</label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {THOUSANDS_OPTIONS.map(o => (
            <label key={o.value} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="thousands"
                value={o.value}
                checked={thousands === o.value}
                onChange={() => set(setThousands)(o.value)}
                style={{ accentColor: green }}
              />
              {o.label}
            </label>
          ))}
        </div>
      </div>

      {/* Currency Display */}
      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle(t)}>Currency Display</label>
        <div style={{ display: 'flex', gap: 12 }}>
          {CURRENCY_DISPLAY_OPTIONS.map(o => (
            <label key={o.value} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="currencyDisplay"
                value={o.value}
                checked={currencyDisplay === o.value}
                onChange={() => set(setCurrencyDisplay)(o.value)}
                style={{ accentColor: green }}
              />
              <span style={{ fontFamily: mono }}>{o.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div style={{
        background: t.bgSurface,
        border: '1px solid ' + t.border,
        borderRadius: 8,
        padding: 20,
        marginBottom: 24,
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, fontFamily: sans, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Live Preview
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: t.textSecondary, fontFamily: sans }}>Positive amount</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: mono }}>
              {formatSample(positiveValue, opts)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: t.textSecondary, fontFamily: sans }}>Negative amount</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#C0392B', fontFamily: mono }}>
              {formatSample(sampleValue, opts)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: t.textSecondary, fontFamily: sans }}>Zero</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.textMuted, fontFamily: mono }}>
              {formatSample(0, opts)}
            </span>
          </div>
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
